import Head from "expo-router/head";
import { useContext } from "react";

import {
  H1,
  H2,
  H3,
  LayoutContext,
  Link,
  List,
  Paragraph,
  ScrollView,
  Section,
  Text,
  Time,
  XStack,
  YStack,
  getTokenValue,
} from "@/components";
import { brandName } from "@/config/brand";

export default function Privacy() {
  const { headerHeight } = useContext(LayoutContext);
  return (
    <>
      <Head>
        <title>{`Privacy Policy — ${brandName}`}</title>
      </Head>
      <ScrollView
        padding="$200"
        width="100%"
        contentContainerStyle={{
          gap: getTokenValue("$300"),
          maxWidth: getTokenValue("$constraint"),
          marginHorizontal: "auto",
          paddingTop: headerHeight.current,
          paddingBottom: getTokenValue("$300"),
        }}
      >
        <YStack gap="$300" alignItems="center">
          <H1>Privacy Policy</H1>
          <XStack>
            <Paragraph display="inline">Last updated:&nbsp;</Paragraph>
            <Time color="$textSecondary" display="inline" dateTime="2026-02-27">
              February 27, 2026
            </Time>
          </XStack>
        </YStack>

        <Paragraph>
          This Privacy Policy (this "Privacy Policy") explains how Curai Health
          and Curai, Inc. and their affiliates ("Curai Health Medical Group",
          "we", "us" or "our") collect, use and share information about you when
          you access or use our <Link href="/">website</Link> ("the Website")
          and the mobile applications ("Apps," and together with the Website,
          the "Services")
        </Paragraph>

        <Text bold>
          This Privacy Policy is not a contract, and does not create any
          contractual rights or obligations.
        </Text>

        <Paragraph>
          We reserve the right to change this Privacy Policy from time to time
          in our sole discretion. If we make changes to this Privacy Policy, we
          will provide notice of such changes by updating the "Last Updated"
          date at the beginning of this Privacy Policy and may provide notice by
          sending an email notification or providing notice through the
          Services. We encourage you to review this Privacy Policy whenever you
          use or access the website or otherwise interact with us to stay
          informed about our information practices and the ways you can help
          protect your privacy.
        </Paragraph>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            A Note About Protected Health Information
          </H2>
          <Paragraph>
            [If you use the Services as a recipient of health care services from
            participating health care providers, we will protect individually
            identifiable health information in accordance with the Health
            Insurance Portability and Accountability Act of 1996 ("HIPAA")
            ("Protected Health Information")]. The types of Protected Health
            Information we may collect includes your demographic and contact
            information, medical history, health-related behaviors, your
            informational exchanges with participating health care providers and
            their responses, suggestions, and guidance, and interactions with
            our digital services. Protected health information may also include
            the information that you have consented to share about you and your
            health by linking other apps, devices, or services—such as the Apple
            HealthKit, fitness trackers, or other health-appliances—to our
            Services.
          </Paragraph>
          <Paragraph>
            This Policy does not govern our use of disclosure of Protected
            Health Information, which is covered by our HIPAA Notice of Privacy
            Practices. For clarity, the HIPAA Notice of Privacy Practices, not
            this Privacy Policy, explains how we use and disclose your Protected
            Health Information.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Collection of Information
          </H2>

          <H3 size="$md" marginBottom="$50">
            Information You Provide
          </H3>
          <Paragraph>
            We collect information you provide directly to us. For example, we
            collect information when you register for an account, participate in
            any interactive features of the Services, subscribe to a newsletter
            or email list, participate in a survey, contest, promotion or event,
            request customer support or otherwise communicate with us.
          </Paragraph>
          <Paragraph>
            The types of information we may collect from you include:
          </Paragraph>
          <List
            listItems={[
              "Account Information. When you register for an account on the Services, you will need to provide us with certain personal information to complete the registration, including information that can be used to contact or identify you, which may include your name, email address, username, password, and phone number. We also collect other information you provide as part of the registration process and the administration and personalization of your user experience, including demographic information such as your gender, age, and zip code. We may combine this information with the personal information we collect from you.",
            ]}
          />

          <H3 size="$md" marginVertical="$50">
            Information We Collect Automatically
          </H3>
          <Paragraph>
            When you access or use the Services, the types of information we may
            automatically collect about you include:
          </Paragraph>
          <List
            listItems={[
              "Usage Information. We collect information about your usage of, and interaction with the Services. For example, we may collect information about how you use and navigate the Services.",
              'Mobile Device Information. We collect information about the mobile device you use to access or use the Services, including the hardware model, operating system and version, mobile network information and information about your use of our Apps. With your consent, we may access and collect information from certain native applications on your device (such as your device\'s camera, photo album and phonebook applications) to facilitate your use of certain features of the Services. For more information about how you can control the collection of location information and/or our access to other applications on your device, please see "Your Choices" below.',
              'Location Information. With your permission, we may also collect information about the precise location of your device using methods that may include GPS, wireless networks, cell towers, and Wi-Fi access points. For more information about how you can control the collection of location information and/or our access to other applications on your device, please see "Your Choices" below.',
              'Log Information. When you access or use the Services, our servers automatically record certain log file information, such as your Internet Protocol ("IP") address, operating system, browser type and language, referring URLs, access times, pages viewed, links clicked and other information about your activities on the Services.',
              "Information Collected by Cookies. We and our service providers use various tracking technologies, including cookies, to collect information about you when you interact with our Website, including information about your browsing and purchasing behavior. Cookies are small data files stored on your hard drive or in device memory that help us improve the Services and your experience, see which areas and features of the Services are popular, and count visits. Most web browsers are set to accept cookies by default. If you prefer, you can usually choose to set your browser to remove or reject browser cookies. Please note that if you choose to remove or reject cookies, this could affect the availability and functionality of the Website.",
            ]}
          />

          <H3 size="$md" marginVertical="$50">
            Information We Collect From Third Parties
          </H3>
          <Paragraph>
            We may receive information other people provide us, which may
            include information about you, in connection with one or more
            business purposes.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Use of Information
          </H2>
          <Paragraph>
            We may use your information for various purposes related to our
            operations and your use of the Services. For example, Curai Health
            Medical Group may use your information to:
          </Paragraph>
          <List
            listItems={[
              "Provide, maintain and improve our Services and provide you with relevant information;",
              "Send you technical notices, updates, security alerts and support and administrative messages;",
              "Respond to your comments, questions and requests and provide customer service;",
              "Monitor and analyze trends, usage and activities in connection with our Services;",
              "Detect, investigate and prevent fraudulent transactions and other illegal activities and protect the rights and property of Curai Health Medical Group and others;",
              "Communicate with you about products, services, offers, promotions, rewards, events, and research study and user feedback opportunities offered by us and others, and provide news and information we think will be of interest to you;",
              "Maintain appropriate records for internal administrative purposes;",
              "Link or combine with information we get from others to help understand your needs and provide you with a better experience; and",
              "Carry out any other purpose described to you at the time the information was collected.",
            ]}
          />
          <Paragraph>
            We also may use aggregated or de-identified information, which
            cannot reasonably be used to identify you. Once de-identified and
            aggregated so that data does not personally identify you (for
            example, we may aggregate data in order to improve our automation
            and improve care), it is no longer personal information. Such
            de-identified and/or aggregated information which does not identify
            individuals is not subject to this Privacy Policy.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Sharing of Information
          </H2>
          <Paragraph>
            We may share information about you as follows or as otherwise
            described in this Privacy Policy:
          </Paragraph>
          <List
            listItems={[
              "With vendors, consultants and other service providers who need access to such information to carry out work or perform the Services on our behalf;",
              "With third party businesses to provide you with information about products and services that may be useful to you;",
              "In response to a request for information if we believe disclosure is in accordance with, or required by, any applicable law, rule, regulation or legal process;",
              "If we believe your actions are inconsistent with our Terms of Use or other policies, or to protect the rights, property or safety of Curai Health Medical Group or others;",
              "In connection with, or during negotiations of, any merger, acquisition, sale of assets or any business, other change of control transaction or financing;",
              "Between and among Curai Health Medical Group and any current or future parent, subsidiary and/or affiliated company; and",
              "With your consent or at your direction.",
            ]}
          />
          <Paragraph>
            We also may share aggregated or de-identified information, which
            cannot reasonably be used to identify you.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Third-Party Analytics
          </H2>
          <Paragraph>
            We partner with certain third-parties to collect, analyze, and use
            some of the information described above. For example, we may allow
            third parties to set cookies or use web beacons on the Services or
            in email communications from Curai Health Medical Group. The
            information collected by third parties using these technologies may
            be used to engage in analysis and reporting. These third parties may
            set and access cookies on your computer or other device and may
            collect information about your online activities across different
            websites or services over time, including on websites and mobile
            applications that are not owned or operated by Curai Health Medical
            Group. In particular, the Services may use Google Analytics to help
            collect and analyze certain information for the purposes discussed
            above. You may review Google Analytics' security and privacy
            principles by clicking{" "}
            <Link href="http://www.google.com/analytics/learn/privacy.html">
              here
            </Link>{" "}
            and opt out of the use of cookies in web browsers by Google
            Analytics by clicking{" "}
            <Link href="https://tools.google.com/dlpage/gaoptout">here</Link>.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            How We Respond to "Do Not Track" Signals
          </H2>
          <Paragraph>
            Some web browsers have "Do Not Track" or similar features that allow
            you to tell each website you visit that you do not want your
            activities on that website tracked. At present, the Services do not
            respond to "Do Not Track" signals and consequently, the Services
            will continue to collect information about you even if your
            browser's "Do Not Track" feature is activated.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Retention of Your Information
          </H2>
          <Paragraph>
            As a general matter, we will keep your personal information for as
            long as necessary to fulfill the purpose for which it was collected.
            If a law requires us to retain your information for a longer period
            of time, we will comply with that law. We will also retain your
            personal information as necessary to protect our legal rights.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Security
          </H2>
          <Paragraph>
            Curai Health Medical Group takes reasonable measures to help protect
            information about you from loss, theft, misuse and unauthorized
            access, disclosure, alteration and destruction. Please understand,
            however, that no security system is impenetrable. We cannot
            guarantee the security of our databases, nor can we guarantee that
            the information you supply will not be intercepted while being
            transmitted to or from us over the Internet. In particular, email
            sent to or from the Services may not be secure, and you should
            therefore take special care in deciding what information you send to
            us via email.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Third-Party Websites
          </H2>
          <Paragraph>
            You may click on links on our Services to access other websites that
            do not operate under this Privacy Policy. These third-party websites
            may independently solicit and collect information, including health
            information, from you and, in some instances, provide us with
            information about your activities on those websites. We recommend
            that you consult the privacy policies of all third-party websites
            you visit. Any access to and use of such third-party websites is not
            governed by this Privacy Policy, but instead is governed by the
            privacy policies of those third-party websites. We are not
            responsible for the information practices of such third-party
            websites.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Your Choices
          </H2>

          <H3 size="$md" marginVertical="$50">
            Reviewing, Correcting, and Deleting Your Personal Information
          </H3>
          <Paragraph>
            You may access, correct, and/or request deletion of certain
            information that you have provided to us by contacting us through
            the Services, or by emailing us at support@curai.com. If you email
            us, for your protection, we may only implement requests with respect
            to the information associated with the particular email address you
            use to send us your request, and we may need to verify your identity
            before implementing your request. We will attempt to comply with any
            reasonable requests for accessing, correcting, or deleting your
            information; however we may be unable to accommodate your request
            under certain circumstances, such as if we need to retain your
            information, including Protected Health Information that is subject
            to our Notice of Privacy Practices, to comply with our legal
            obligations.
          </Paragraph>
          <Paragraph>
            While we may be unable to delete certain pieces of information from
            your medical record, at your request, we can de-activate your secure
            account so that you and others can no longer access it with your
            username and password.
          </Paragraph>

          <H3 size="$md" marginVertical="$50">
            Location Information
          </H3>
          <Paragraph>
            With your consent, we may collect information about your actual
            location when you use our Apps. You may stop the collection of this
            information at any time by changing the settings on your mobile
            device, but note that some features of our Apps may no longer
            function if you do so.
          </Paragraph>

          <H3 size="$md" marginVertical="$50">
            Native Applications on Mobile Device
          </H3>
          <Paragraph>
            Some features of our Apps may require access to certain native
            applications on your mobile device, such as the camera, photo album
            and the address book applications. If you decide to use these
            features, we will ask you for your consent prior to accessing the
            applications and collecting associated information. Note that you
            can revoke your consent at any time by changing the settings on your
            device.
          </Paragraph>

          <H3 size="$md" marginVertical="$50">
            Cookies
          </H3>
          <Paragraph>
            Most web browsers are set to accept cookies by default. If you
            prefer, you can usually choose to set your browser to remove or
            reject browser cookies. Please note that if you choose to remove or
            reject cookies, this could affect the availability and functionality
            of the Services.
          </Paragraph>

          <H3 size="$md" marginVertical="$50">
            Push Notifications
          </H3>
          <Paragraph>
            With your consent, we may send promotional and non-promotional push
            notifications or alerts to your mobile device. You can deactivate
            these messages at any time by changing the notification settings on
            your mobile device or within our Apps.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Children's Privacy/Notice for Minors
          </H2>
          <Paragraph>
            We take seriously our obligations under applicable laws concerning
            the collection of information from children under 13. Our services
            are neither intended for nor directed at children under 13. If you
            believe a child under 13 has provided us with Personal Information,
            please contact us at privacy@curaihealth.com.
          </Paragraph>
          <Paragraph>
            Additionally, if you are a California resident under 18 years old
            and a registered user, you can request that we remove content or
            information that you have posted to our website or other online
            services. If you would like to request removal of content or
            information, please contact us at privacy@curaihealth.com. Please
            note that the removal of content may not ensure complete or
            comprehensive removal of that content or information posted through
            the services.
          </Paragraph>
        </Section>

        <Section>
          <H2 size="$lg" marginBottom="$50">
            Questions or Comments
          </H2>
          <Paragraph>
            If you have any questions or comments regarding this Privacy Policy,
            please email or mail us at:
          </Paragraph>
          <Paragraph>
            <Link href="mailto:privacy@curaihealth.com">
              privacy@curaihealth.com
            </Link>
          </Paragraph>
          <Paragraph>
            Privacy Administrator, Curai Health Medical Group
          </Paragraph>
          <Paragraph>
            2443 Fillmore St. #380-15799, San Francisco, CA 94114
          </Paragraph>
          <Text size="$sm" color="$textSecondary">
            Ver 1.0 2.27.26
          </Text>
        </Section>
      </ScrollView>
    </>
  );
}
